interface SinceLastVisitSummary {
  quiet: boolean;
  changedAdoptionsCount: number;
  newNotificationsCount: number;
  unreadNotificationsCount: number;
  lastDashboardViewedAt: number | null;
  generatedAt: number;
}

interface WatchlistSummaryStripProps {
  sinceLastVisit: SinceLastVisitSummary | null;
  adoptionsCount: number;
  totalOfficials: number;
  unreadAlerts: number;
  role?: string | null;
}

function formatLastSeen(timestamp: number | null): string {
  if (!timestamp) return 'First watchlist check';
  return new Date(timestamp).toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function WatchlistSummaryStrip({
  sinceLastVisit,
  adoptionsCount,
  totalOfficials,
  unreadAlerts,
  role,
}: WatchlistSummaryStripProps) {
  const headline = !sinceLastVisit
    ? 'Your watchlist is loading fresh movement and alert state.'
    : sinceLastVisit.quiet
      ? 'Quiet watchlist. No new movement since your last check.'
      : `${sinceLastVisit.changedAdoptionsCount} watched official${sinceLastVisit.changedAdoptionsCount === 1 ? '' : 's'} changed and ${sinceLastVisit.newNotificationsCount} alert${sinceLastVisit.newNotificationsCount === 1 ? '' : 's'} arrived.`;

  const cards = [
    { label: 'Watched Officials', value: adoptionsCount },
    { label: 'Network Coverage', value: totalOfficials > 0 ? totalOfficials : '—' },
    {
      label: 'Unread Alerts',
      value: sinceLastVisit ? sinceLastVisit.unreadNotificationsCount : unreadAlerts,
    },
    { label: 'Role', value: role || 'citizen' },
  ];

  return (
    <section className="glass-panel rounded-lg p-5 mb-8">
      <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
        <div className="max-w-3xl">
          <p className="text-xs font-black uppercase tracking-widest text-gold-600 dark:text-gold-400 mb-1">
            Watchlist Summary
          </p>
          <h2 className="text-2xl font-black uppercase tracking-tight text-navy-900 dark:text-cream-50 mb-2">
            {headline}
          </h2>
          <p className="text-sm text-navy-500 dark:text-cream-300">
            Last checked {formatLastSeen(sinceLastVisit?.lastDashboardViewedAt ?? null)}
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3 lg:min-w-[26rem]">
          {cards.map((card) => (
            <div key={card.label} className="rounded-lg bg-cream-100 p-3 dark:bg-navy-800/60">
              <p className="text-[10px] font-black uppercase tracking-widest text-navy-400 dark:text-navy-300 mb-1">
                {card.label}
              </p>
              <p className="text-2xl font-black capitalize text-navy-900 dark:text-cream-50">
                {card.value}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
